import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "@/components/app/Logo";

function LegalLayout({ title, children }) {
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo /></Link>
          <Link to="/" className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1">
            <ArrowLeft className="h-4 w-4" />Retour
          </Link>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-deep tracking-tight mb-8">{title}</h1>
        <article className="bg-white rounded-2xl border border-slate-200 p-8 prose prose-slate max-w-none">
          {children}
        </article>
        <p className="mt-8 text-xs text-slate-400 text-center">Dernière mise à jour : février 2026</p>
      </main>
    </div>
  );
}

export function MentionsLegales() {
  return (
    <LegalLayout title="Mentions légales">
      <h2>Éditeur</h2>
      <p>
        <strong>Association DOCTOBOOK</strong><br />
        Association loi 1901 à but non lucratif<br />
        SIREN : <strong>922 912 126</strong><br />
        SIRET (siège) : <strong>922 912 126 00013</strong><br />
        Siège social : <strong>39 Boulevard Georges Clemenceau, 83000 Toulon</strong><br />
        N° RNA (Répertoire National des Associations) : <strong>W832021177</strong><br />
        Déclarée à la préfecture du Var (83)<br />
        Date de création : <strong>16 janvier 2023</strong><br />
        N° TVA intracommunautaire : <strong>FR12 922 912 126</strong><br />
        Président / Directeur de la publication : <strong>M. Frédéric André GERARD</strong><br />
        Contact : <a href="mailto:contact@doctobook.com">contact@doctobook.com</a>
      </p>

      <h2>Hébergement — HDS</h2>
      <p>
        Doctobook est hébergé par <strong>OVHcloud SAS</strong>, 2 rue Kellermann, 59100 Roubaix, France.<br />
        Certification <strong>Hébergeur de Données de Santé (HDS)</strong> conformément à l'article L.1111-8 du Code de la Santé Publique.
      </p>

      <h2>Nature du service</h2>
      <p>
        Doctobook est un <strong>logiciel en mode SaaS</strong> (Software as a Service) destiné aux professionnels de santé et aux patients français.
        Il propose :
      </p>
      <ul>
        <li>Un réseau social professionnel santé (gratuit)</li>
        <li>Un annuaire national de tous les professionnels de santé et établissements (source : RPPS de l'ANS et FINESS de data.gouv.fr — licence Etalab)</li>
        <li>Des outils cabinet payants : prise de rendez-vous, téléconsultation, DMP, messagerie chiffrée, ordonnance numérique</li>
      </ul>

      <h2>Absence d'intermédiation financière sur les honoraires</h2>
      <p className="bg-emerald-50 border-l-4 border-emerald-500 p-4 rounded">
        <strong>Doctobook ne perçoit aucune commission sur les honoraires médicaux.</strong> Conformément à l'article R.4127-53 du Code de la Santé Publique
        (Code de déontologie médicale), les honoraires du médecin sont demandés au malade sans aucune sollicitation étrangère.
        Doctobook facture uniquement les professionnels de santé au forfait mensuel de leur plan (abonnement SaaS).
        Les honoraires vont directement du patient au professionnel de santé, sans transit par les comptes de Doctobook.
      </p>

      <h2>Propriété intellectuelle</h2>
      <p>
        L'ensemble du contenu de Doctobook (design, code source, logo, marques, textes) est protégé par le Code de la propriété intellectuelle.
        Toute reproduction ou représentation, totale ou partielle, sans autorisation écrite préalable est interdite.
      </p>

      <h2>Signalement de contenu illicite</h2>
      <p>
        Pour signaler un contenu illicite conformément à l'article 6.I.3 de la LCEN :
        <a href="mailto:signalement@doctobook.com">signalement@doctobook.com</a>
      </p>
    </LegalLayout>
  );
}

export function CGU() {
  return (
    <LegalLayout title="Conditions Générales d'Utilisation">
      <h2>1. Objet</h2>
      <p>
        Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'utilisation de la plateforme Doctobook accessible depuis
        doctobook.com et doctobook.fr, ainsi que leurs applications mobiles associées.
      </p>

      <h2>2. Acceptation</h2>
      <p>
        L'inscription à Doctobook implique l'acceptation sans réserve des présentes CGU. En cas de désaccord, l'utilisateur ne doit pas créer de compte.
      </p>

      <h2>3. Éligibilité</h2>
      <p>
        Les patients doivent être majeurs (18 ans) ou mineurs représentés par un tuteur légal. Les professionnels de santé doivent être inscrits
        au Répertoire Partagé des Professionnels de Santé (RPPS) ou disposer d'une pièce équivalente délivrée par leur Ordre professionnel.
      </p>

      <h2>4. Rôles et responsabilités</h2>
      <p>
        <strong>Doctobook</strong> fournit un outil de mise en relation et de communication. Elle n'est pas prestataire d'actes médicaux et ne
        se substitue pas au professionnel de santé.<br />
        <strong>Le professionnel de santé</strong> conserve l'entière responsabilité de son exercice, de sa relation avec ses patients et de
        ses honoraires.<br />
        <strong>Le patient</strong> est responsable de la véracité des informations qu'il communique.
      </p>

      <h2>5. Modération et signalement</h2>
      <p>
        Tout contenu publié sur Doctobook (posts, cas cliniques, commentaires) doit respecter le Code de déontologie médicale, la vie privée
        des tiers et les lois en vigueur. Un signalement en 6 catégories est disponible sur chaque publication. Les cas cliniques font l'objet d'une
        modération humaine systématique avec checklist d'anonymisation obligatoire (SLA 4 h).
      </p>

      <h2>6. Absence d'avis médical personnalisé</h2>
      <p>
        Les échanges publics sur Doctobook (Q&amp;A, articles, commentaires) ne constituent pas un avis médical personnalisé et n'engagent pas
        la responsabilité des professionnels qui y répondent. En cas d'urgence, appelez le 15 (SAMU) ou le 112.
      </p>

      <h2>7. Abonnements et paiement</h2>
      <p>
        Les plans payants (Standard 69 € HT / mois, Premium 129 € HT / mois) sont facturés au professionnel de santé par prélèvement SEPA mensuel
        (mandat SEPA Core signé électroniquement via GoCardless, établissement de paiement agréé). Résiliable à tout moment sans frais.
        L'essai de 30 jours est sans engagement : aucun prélèvement n'est effectué avant la fin de la période d'essai.
        Les revenus issus de ces abonnements financent exclusivement le fonctionnement et le développement de l'association DOCTOBOOK,
        conformément à son objet statutaire de mise à disposition d'outils numériques pour la santé.
      </p>

      <h2>8. Résiliation</h2>
      <p>
        Chaque partie peut résilier son compte à tout moment. À la résiliation, un compte payant bascule automatiquement sur le plan Communauté
        gratuit. Les données professionnelles sont conservées 30 jours puis anonymisées, sauf demande contraire.
      </p>

      <h2>9. Modification des CGU</h2>
      <p>
        Doctobook se réserve le droit de modifier ces CGU. Toute modification substantielle sera notifiée par email 30 jours avant application.
      </p>

      <h2>10. Loi applicable et juridiction</h2>
      <p>
        Les présentes CGU sont soumises au droit français. Tout litige sera de la compétence exclusive des tribunaux du siège social de l'association DOCTOBOOK (SIREN 922 912 126).
      </p>
    </LegalLayout>
  );
}

export function Privacy() {
  return (
    <LegalLayout title="Politique de confidentialité (RGPD)">
      <h2>Responsable de traitement</h2>
      <p>
        Le responsable de traitement est l'<strong>Association DOCTOBOOK</strong> (loi 1901 · SIRET 922 912 126 00013 · RNA W832021177),
        dont le siège est situé au 39 Boulevard Georges Clemenceau, 83000 Toulon, représentée par son président M. Frédéric André GERARD.<br />
        Délégué à la Protection des Données (DPO) : <a href="mailto:dpo@doctobook.com">dpo@doctobook.com</a>
      </p>

      <h2>Données collectées</h2>
      <p>Nous collectons uniquement les données strictement nécessaires à la fourniture du service :</p>
      <ul>
        <li><strong>Compte</strong> : email, mot de passe hashé, nom, prénom, téléphone, rôle</li>
        <li><strong>Professionnels</strong> : numéro RPPS, spécialité, adresse cabinet, pièces justificatives (chiffrées)</li>
        <li><strong>Patients</strong> : informations médicales partagées volontairement (DMP), rendez-vous, échanges chiffrés</li>
        <li><strong>Techniques</strong> : logs d'accès (IP anonymisée après 12 mois), cookies fonctionnels uniquement</li>
      </ul>

      <h2>Bases légales</h2>
      <ul>
        <li>Exécution du contrat (fourniture du service)</li>
        <li>Consentement (communications, partages DMP)</li>
        <li>Obligation légale (facturation, archivage santé 10 ans)</li>
      </ul>

      <h2>Vos droits RGPD</h2>
      <p>
        Vous disposez à tout moment des droits d'<strong>accès, rectification, effacement, limitation, portabilité et opposition</strong>.
        Pour exercer ces droits : <a href="mailto:dpo@doctobook.com">dpo@doctobook.com</a>. Réponse sous 30 jours.
        En cas de désaccord, vous pouvez saisir la <a href="https://www.cnil.fr" target="_blank" rel="noreferrer">CNIL</a>.
      </p>

      <h2>Hébergement et sécurité</h2>
      <p>
        Toutes vos données sont hébergées en France chez <strong>OVHcloud avec certification HDS</strong> (Hébergeur de Données de Santé).
        Chiffrement au repos AES-256. Messagerie chiffrée de bout en bout. Audit logs immuables. Aucun transfert hors UE.
      </p>

      <h2>Cookies</h2>
      <p>
        Doctobook utilise uniquement des cookies techniques strictement nécessaires (session, préférences de langue). Aucun cookie publicitaire,
        aucun tracker tiers.
      </p>

      <h2>Conservation</h2>
      <ul>
        <li>Compte actif : durée de l'abonnement</li>
        <li>Après résiliation : 30 j puis anonymisation, sauf obligations légales</li>
        <li>Données de santé : 10 ans (Code de Santé Publique)</li>
        <li>Logs de sécurité : 12 mois</li>
      </ul>
    </LegalLayout>
  );
}
